import { useCallback, useEffect, useState } from 'react'
import { api } from '../api/client'

// Mirrors `backend/server/features/people_router.ProjectPersonItem` rows in
// the ranked response. `data` is the full collection_items payload so the
// avatar/name/relation renderer is the same as the alphabetical section.
export interface RankedPersonItem {
    collection_item_id: number
    data: Record<string, any>
    checkin_count: number
}

export interface RankedPeopleResponse {
    lifetime: RankedPersonItem[]
    recent: RankedPersonItem[]
    recent_days: number
}

/**
 * Shared fetch for `/api/people/ranked`. Used by PeopleCollectionPage (full
 * surface), CollectionsPage (preview rails at the top), and the checkin-edit
 * `ReferenceSelector`'s People empty state. The server caps each rail at
 * `limit` rows; pass the same `RANKED_MAX_COUNT` from the rail UI so "Show
 * more" doesn't run out before reaching the limit.
 *
 * `enabled: false` skips the fetch entirely (no loading state) — for callers
 * that only conditionally show people (e.g. a generic reference picker whose
 * collection turns out not to be People; the endpoint would otherwise
 * side-effect a People collection into existence via
 * `get_or_create_people_collection`).
 */
export const useRankedPeople = (limit: number = 10, enabled: boolean = true) => {
    const [ranked, setRanked] = useState<RankedPeopleResponse | null>(null)
    const [loading, setLoading] = useState(enabled)
    const [error, setError] = useState<string | null>(null)

    const reload = useCallback(async () => {
        if (!enabled) return
        try {
            setLoading(true)
            const resp = await api.get<RankedPeopleResponse>(
                `/api/people/ranked?limit=${limit}`,
            )
            setRanked(resp.data)
            setError(null)
        } catch (err) {
            // Non-fatal: rails are a nicety on top of the alpha grid / list.
            console.error('Failed to load ranked people:', err)
            setError('Failed to load ranked people.')
        } finally {
            setLoading(false)
        }
    }, [limit, enabled])

    useEffect(() => {
        reload()
    }, [reload])

    return { ranked, loading, error, reload }
}
